import { useNavigate } from '@tanstack/react-router';
import { useBillingSummary } from './queries';
import { PlanCardUsage } from './PlanCardUsage';
import { PlanCardPendingChange } from './PlanCardPendingChange';
import { trackTrialBannerInteracted } from './trial-banner-analytics';
import type { BillingSummary } from '@/lib/billing';

/**
 * Plan block inside the sidebar's workspace card, under the switcher.
 *
 * Render strategy:
 * - Loading → a quiet skeleton bar (the card must never flash empty space).
 * - Error   → a small "Plan unavailable" line. The sidebar can't crash the
 *             shell, so this never propagates to an error block or toast.
 * - Loaded  → the daily sending meter, then only the plan lines that matter
 *             right now: trial days left, a pending cancel/pause/resume, or
 *             a free plan — each with an Upgrade link where it applies.
 *
 * The plan name and status chip live on `/billing`, not in the rail.
 * Visibility is owned by the caller — the collapsed rail skips this block.
 */
function shouldShowUpgrade(summary: BillingSummary): boolean {
  if (summary.status === 'TRIALING') return true;
  if (summary.plan == null) return true;
  if (summary.plan.name.toLowerCase() === 'free') return true;
  return false;
}

export function PlanCard() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useBillingSummary();

  if (isLoading) {
    return (
      <div aria-busy="true" className="flex flex-col gap-[7px]">
        <div className="h-[5px] rounded-full bg-[color:var(--ink-100)]" />
        <div className="h-[10px] w-3/5 animate-pulse rounded bg-[color:var(--ink-100)]" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div role="status" className="sidebar-plan-line">
        Plan unavailable
      </div>
    );
  }

  const showUpgrade = shouldShowUpgrade(data);
  const upgrade = showUpgrade ? (
    <button
      type="button"
      className="sidebar-plan-upgrade"
      onClick={() => {
        trackTrialBannerInteracted(data, 'upgrade_clicked');
        void navigate({ to: '/billing', search: { tab: 'plans' } });
      }}
    >
      Upgrade plan
    </button>
  ) : null;

  const planLine = data.trial ? (
    <span>
      <span className="num">{data.trial.daysLeft}</span>{' '}
      {data.trial.daysLeft === 1 ? 'day' : 'days'} left in trial
    </span>
  ) : showUpgrade ? (
    <span className="truncate" title={data.plan?.name ?? 'No plan'}>
      {data.plan?.name ?? 'No plan'}
    </span>
  ) : null;

  return (
    <div className="flex flex-col gap-2">
      <PlanCardUsage usage={data.usage} warmupDaily={data.warmupDaily} />

      {/* Owner-gated CTA lives inside — see PlanCardPendingChange. */}
      <PlanCardPendingChange
        scheduledChange={data.scheduledChange}
        scheduledResumeAt={data.scheduledResumeAt}
      />

      {planLine && (
        <div className="sidebar-plan-line">
          {planLine}
          {upgrade}
        </div>
      )}
    </div>
  );
}
